import { c as _c } from "react/compiler-runtime";
import type { HofDashboardData, HofDashboardForm } from '@/features/hof-dashboard/api/hof-dashboard-data';
import { AreaIcon, LABEL_CLASS, Panel, ProgressPill, QUIET_BUTTON_CLASS, SECONDARY_BUTTON_CLASS, SectionHeading, SegmentBar } from '@/features/hof-dashboard/components/dashboard-ui';
import { DeadlineTimeline, dueByDay } from '@/features/hof-dashboard/components/deadline-list';
import { DocumentLinks } from '@/features/hof-dashboard/components/document-links';
import { HOF_DASHBOARD_AREA_LABELS, HOF_DASHBOARD_AREAS, type HofDashboardArea } from '@/features/hof-dashboard/constants';
import { formatCountdown, formatDate, translate, translateCount, type TextKey } from '@/features/hof-dashboard/texts';
import { isOpen, type SubmissionProgress } from '@/features/hof-dashboard/utils/submission-progress';
import type { Locale } from '@/types/types';
import { cn } from '@/utils/tailwindcss-override';
import { CheckCircle2, ChevronRight, Clock, Lock } from 'lucide-react';
import type React from 'react';

/** Documents the overview lists; the rest are a tap away under "Dokumente". */
const OVERVIEW_DOCUMENTS = 3;
interface FormWithProgress {
  form: HofDashboardForm;
  progress: SubmissionProgress;
}

/** Soonest first; a form without a deadline goes last. */
const byDeadline = (a: FormWithProgress, b: FormWithProgress): number => (a.progress.deadline ?? '9999').localeCompare(b.progress.deadline ?? '9999');

/** The open forms by their due date, soonest first, those without one last. */
const groupByDeadline = (open: readonly FormWithProgress[]): {
  deadline: string | undefined;
  forms: FormWithProgress[];
}[] => {
  const groups: {
    deadline: string | undefined;
    forms: FormWithProgress[];
  }[] = [];
  for (const entry of open.toSorted(byDeadline)) {
    const last = groups.at(-1);
    // the same day, whatever the time an editor stored with it
    const day = entry.progress.daysLeft;
    if (last !== undefined && last.forms[0]?.progress.daysLeft === day) last.forms.push(entry);else groups.push({
      deadline: entry.progress.deadline,
      forms: [entry]
    });
  }
  return groups;
};

/** What the row's button does: hand in, revise, or order. */
const rowAction = ({
  form,
  progress
}: FormWithProgress): TextKey => {
  if (progress.gap === 'revision') return 'revise';
  if (form.area === 'material') return form.entries.length === 0 ? 'placeOrder' : 'changeOrder';
  return 'handIn';
};

/** The area, and what the Hof handed in so far or how the form counts. */
const rowDetail = (form: HofDashboardForm, locale: Locale): string => {
  const parts = [HOF_DASHBOARD_AREA_LABELS[form.area][locale]];
  const newest = form.entries[0];
  if (form.mode === 'entries' && form.entries.length > 0) {
    parts.push(translate('entriesCount', locale, {
      n: form.entries.length
    }));
  } else if (newest !== undefined) {
    parts.push(translate('versionOn', locale, {
      n: form.entries.length,
      date: formatDate(newest.submittedAt, locale)
    }));
  } else if (form.area === 'material' && form.mode === 'versions') {
    parts.push(translate('newestOrderCounts', locale));
  }
  return parts.join(' · ');
};

/** One form in the overview's list, with the button that leads to it. */
const FormRow: React.FC<{
  entry: FormWithProgress;
  locale: Locale;
  onOpen: () => void;
}> = t0 => {
  const $ = _c(43);
  const {
    entry,
    locale,
    onOpen
  } = t0;
  const {
    form,
    progress
  } = entry;
  let t1;
  if ($[0] !== progress) {
    t1 = isOpen(progress);
    $[0] = progress;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const open = t1;
  let t2;
  if ($[2] !== form.area) {
    t2 = <AreaIcon area={form.area} className="mt-1" />;
    $[2] = form.area;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== form.title) {
    t3 = <p className="text-base font-semibold text-balance text-green-900">{form.title}</p>;
    $[4] = form.title;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== form || $[7] !== locale) {
    t4 = rowDetail(form, locale);
    $[6] = form;
    $[7] = locale;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = <p className="text-sm text-green-700">{t4}</p>;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== locale || $[12] !== progress) {
    t6 = <p className="pt-1.5 @min-[640px]:hidden"><ProgressPill progress={progress} locale={locale} /></p>;
    $[11] = locale;
    $[12] = progress;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t3 || $[15] !== t5 || $[16] !== t6) {
    t7 = <div className="min-w-0">{t3}{t5}{t6}</div>;
    $[14] = t3;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t2 || $[19] !== t7) {
    t8 = <div className="flex min-w-0 items-start gap-3">{t2}{t7}</div>;
    $[18] = t2;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== locale || $[22] !== progress) {
    t9 = <div className="hidden @min-[640px]:block"><ProgressPill progress={progress} locale={locale} /></div>;
    $[21] = locale;
    $[22] = progress;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const t10 = open ? SECONDARY_BUTTON_CLASS : QUIET_BUTTON_CLASS;
  let t11;
  if ($[24] !== t10) {
    t11 = cn(t10, "ml-5 justify-self-start @min-[420px]:ml-0 @min-[420px]:justify-self-end");
    $[24] = t10;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== form.title || $[27] !== locale) {
    t12 = translate("openFormLabel", locale, {
      form: form.title
    });
    $[26] = form.title;
    $[27] = locale;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== entry || $[30] !== locale || $[31] !== open) {
    t13 = translate(open ? rowAction(entry) : "view", locale);
    $[29] = entry;
    $[30] = locale;
    $[31] = open;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <ChevronRight aria-hidden={true} />;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== onOpen || $[35] !== t11 || $[36] !== t12 || $[37] !== t13) {
    t15 = <button type="button" className={t11} aria-label={t12} onClick={onOpen}>{t13}{t14}</button>;
    $[34] = onOpen;
    $[35] = t11;
    $[36] = t12;
    $[37] = t13;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== t15 || $[40] !== t8 || $[41] !== t9) {
    t16 = <li className="grid items-center gap-x-4 gap-y-3 px-5 py-4 @min-[420px]:grid-cols-[minmax(0,1fr)_auto] @min-[640px]:grid-cols-[minmax(0,1fr)_auto_auto] @xl:px-6">{t8}{t9}{t15}</li>;
    $[39] = t15;
    $[40] = t8;
    $[41] = t9;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  return t16;
};

/** The head of a group in the list: its icon, its name, and how much is in it. */
const GroupHead: React.FC<{
  icon: React.ReactNode;
  title: string;
  detail: string;
  tone?: 'alert' | undefined;
}> = t0 => {
  const $ = _c(16);
  const {
    icon,
    title,
    detail,
    tone
  } = t0;
  const t1 = tone === "alert" ? "text-danger" : "text-green-700";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("[&_svg]:h-4 [&_svg]:w-4", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== icon || $[3] !== t2) {
    t3 = <span aria-hidden={true} className={t2}>{icon}</span>;
    $[2] = icon;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = tone === "alert" ? "text-danger-text" : "text-green-900";
  let t5;
  if ($[5] !== t4) {
    t5 = cn("font-semibold", t4);
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t5 || $[8] !== title) {
    t6 = <span className={t5}>{title}</span>;
    $[7] = t5;
    $[8] = title;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== detail) {
    t7 = <span className="text-green-700">{detail}</span>;
    $[10] = detail;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t3 || $[13] !== t6 || $[14] !== t7) {
    t8 = <li className="bg-page flex flex-wrap items-center gap-x-3 gap-y-1 px-5 py-2.5 text-sm @xl:px-6">{t3}{t6}{t7}</li>;
    $[12] = t3;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
};

/** How much is open, when it is next due, and how far each area is: the first line to read. */
const SummaryStrip: React.FC<{
  data: HofDashboardData;
  entries: readonly FormWithProgress[];
  locale: Locale;
  onOpenArea: (area: HofDashboardArea) => void;
}> = t0 => {
  const $ = _c(18);
  const {
    data,
    entries,
    locale,
    onOpenArea
  } = t0;
  let t1;
  if ($[0] !== data || $[1] !== entries || $[2] !== locale || $[3] !== onOpenArea) {
    const open = entries.filter(_temp).toSorted(byDeadline);
    const next = open.find(_temp2)?.progress;
    let t2;
    if ($[5] !== data) {
      t2 = area => data.forms.some(form => form.area === area);
      $[5] = data;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    const areas = HOF_DASHBOARD_AREAS.filter(t2);
    let t3;
    if ($[7] !== locale) {
      t3 = translate("stillOpen", locale);
      $[7] = locale;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    let t4;
    if ($[9] !== t3) {
      t4 = <p className={LABEL_CLASS}>{t3}</p>;
      $[9] = t3;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    const t5 = open.length === 0 ? <p className="flex items-center gap-2 pt-1 text-base text-green-700"><CheckCircle2 className="h-5 w-5" aria-hidden={true} />{translate("allDone", locale)}</p> : <><p className="flex items-baseline gap-2"><span className="font-heading text-heading text-4xl leading-none font-extrabold tabular-nums">{open.length}</span><span className="text-base font-medium text-green-900">{translate(open.length === 1 ? "submissionOne" : "submissionMany", locale)}</span></p>{next?.deadline !== undefined && next.daysLeft !== undefined && <p className={cn("text-sm", next.daysLeft < 0 ? "text-danger-text" : "text-green-700")}>{translate("nextDeadline", locale)}{" "}<span className="font-semibold tabular-nums">{formatDate(next.deadline, locale)}</span>{" "}· {formatCountdown(next.daysLeft, locale)}</p>}</>;
    let t6;
    if ($[11] !== t4 || $[12] !== t5) {
      t6 = <div className="space-y-1 p-5 @xl:p-6">{t4}{t5}</div>;
      $[11] = t4;
      $[12] = t5;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    let t7;
    if ($[14] !== entries || $[15] !== locale || $[16] !== onOpenArea) {
      t7 = area_0 => {
        const ofArea = entries.filter(t8 => {
          const {
            form: form_0
          } = t8;
          return form_0.area === area_0;
        });
        const done = ofArea.filter(_temp3).length;
        const count = translate("progressCount", locale, {
          done,
          total: ofArea.length
        });
        const label = HOF_DASHBOARD_AREA_LABELS[area_0][locale];
        return <button key={area_0} type="button" className="cursor-pointer space-y-3 p-5 text-left transition hover:bg-gray-50 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-green-600 @xl:p-6" onClick={() => onOpenArea(area_0)}><span className="flex items-center gap-2 text-base font-semibold text-green-900"><AreaIcon area={area_0} />{label}</span><span className="block text-sm text-green-700 tabular-nums">{count}</span><SegmentBar label={`${label}: ${count}`} segments={ofArea.map(_temp4)} /></button>;
      };
      $[14] = entries;
      $[15] = locale;
      $[16] = onOpenArea;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    t1 = <Panel className="divide-line grid divide-y p-0 @min-[640px]:auto-cols-fr @min-[640px]:grid-flow-col @min-[640px]:divide-x @min-[640px]:divide-y-0 @xl:p-0">{t6}{areas.map(t7)}</Panel>;
    $[0] = data;
    $[1] = entries;
    $[2] = locale;
    $[3] = onOpenArea;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};

/**
 * What the Hof sees first: how much is open and when it is next due, then everything it has
 * to hand in grouped by deadline, each with the button that leads to it, and beside it the
 * deadlines as a timeline and the documents. What is done is listed last.
 */
export const OverviewView: React.FC<{
  data: HofDashboardData;
  progress: Record<string, SubmissionProgress>;
  locale: Locale;
  onOpen: (area: HofDashboardArea, formId: string) => void;
  onOpenArea: (area: HofDashboardArea) => void;
  onOpenDocuments: () => void;
}> = t0 => {
  const $ = _c(74);
  const {
    data,
    progress,
    locale,
    onOpen,
    onOpenArea,
    onOpenDocuments
  } = t0;
  let T0;
  let due;
  let t1;
  let t10;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== data || $[1] !== locale || $[2] !== onOpen || $[3] !== onOpenArea || $[4] !== progress) {
    let t11;
    if ($[17] !== progress) {
      t11 = form => {
        const formProgress = progress[form.id];
        return formProgress === undefined ? [] : [{
          form,
          progress: formProgress
        }];
      };
      $[17] = progress;
      $[18] = t11;
    } else {
      t11 = $[18];
    }
    const entries = data.forms.flatMap(t11);
    const open = entries.filter(_temp5);
    const done = entries.filter(_temp6);
    const closed = entries.filter(_temp7);
    due = dueByDay(entries.map(_temp8), new Date());
    let t12;
    if ($[19] !== locale || $[20] !== onOpen) {
      t12 = entry_3 => <FormRow key={entry_3.form.id} entry={entry_3} locale={locale} onOpen={() => onOpen(entry_3.form.area, entry_3.form.id)} />;
      $[19] = locale;
      $[20] = onOpen;
      $[21] = t12;
    } else {
      t12 = $[21];
    }
    const row = t12;
    t9 = "space-y-6";
    if ($[22] !== data || $[23] !== entries || $[24] !== locale || $[25] !== onOpenArea) {
      t10 = <SummaryStrip data={data} entries={entries} locale={locale} onOpenArea={onOpenArea} />;
      $[22] = data;
      $[23] = entries;
      $[24] = locale;
      $[25] = onOpenArea;
      $[26] = t10;
    } else {
      t10 = $[26];
    }
    t8 = "grid items-start gap-6 @min-[760px]:grid-cols-[minmax(0,1fr)_minmax(15rem,18rem)]";
    T0 = Panel;
    t6 = "@container p-0 @xl:p-0";
    let t13;
    if ($[27] !== locale) {
      t13 = translate("openSubmissions", locale);
      $[27] = locale;
      $[28] = t13;
    } else {
      t13 = $[28];
    }
    let t14;
    if ($[29] !== t13) {
      t14 = <SectionHeading className="text-xl">{t13}</SectionHeading>;
      $[29] = t13;
      $[30] = t14;
    } else {
      t14 = $[30];
    }
    const t15 = open.length > 1 && <span className="text-sm text-green-700">{translate("sortedByDeadline", locale)}</span>;
    if ($[31] !== t14 || $[32] !== t15) {
      t7 = <div className="flex flex-wrap items-baseline justify-between gap-2 px-5 pt-5 pb-4 @xl:px-6 @xl:pt-6">{t14}{t15}</div>;
      $[31] = t14;
      $[32] = t15;
      $[33] = t7;
    } else {
      t7 = $[33];
    }
    t1 = "divide-line border-line divide-y border-t";
    t2 = open.length === 0 && <li className="flex items-center gap-2 px-5 py-4 text-base text-green-700 @xl:px-6"><CheckCircle2 className="h-4 w-4" aria-hidden={true} />{translate("allDone", locale)}</li>;
    let t16;
    if ($[34] !== locale || $[35] !== row) {
      t16 = group => {
        const daysLeft = group.forms[0]?.progress.daysLeft;
        const count = translateCount(group.forms.length, "submissionCountOne", "submissionCountMany", locale);
        return [<GroupHead key={`head-${group.deadline ?? "none"}`} icon={<Clock />} tone={daysLeft !== undefined && daysLeft < 0 ? "alert" : undefined} title={group.deadline === undefined ? translate("noDeadline", locale) : translate("dueOn", locale, {
          date: formatDate(group.deadline, locale)
        })} detail={daysLeft === undefined ? count : `${formatCountdown(daysLeft, locale)} · ${count}`} />, ...group.forms.map(entry_4 => row(entry_4))];
      };
      $[34] = locale;
      $[35] = row;
      $[36] = t16;
    } else {
      t16 = $[36];
    }
    t3 = groupByDeadline(open).map(t16);
    t4 = done.length > 0 && [<GroupHead key="head-done" icon={<CheckCircle2 />} title={translate("doneGroup", locale)} detail={translateCount(done.length, "submissionCountOne", "submissionCountMany", locale)} />, ...done.map(entry_5 => row(entry_5))];
    t5 = closed.length > 0 && [<GroupHead key="head-closed" icon={<Lock />} title={translate("gapClosed", locale)} detail={translateCount(closed.length, "submissionCountOne", "submissionCountMany", locale)} />, ...closed.map(entry_6 => row(entry_6))];
    $[0] = data;
    $[1] = locale;
    $[2] = onOpen;
    $[3] = onOpenArea;
    $[4] = progress;
    $[5] = T0;
    $[6] = due;
    $[7] = t1;
    $[8] = t10;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    T0 = $[5];
    due = $[6];
    t1 = $[7];
    t10 = $[8];
    t2 = $[9];
    t3 = $[10];
    t4 = $[11];
    t5 = $[12];
    t6 = $[13];
    t7 = $[14];
    t8 = $[15];
    t9 = $[16];
  }
  let t11;
  if ($[37] !== t1 || $[38] !== t2 || $[39] !== t3 || $[40] !== t4 || $[41] !== t5) {
    t11 = <ul className={t1}>{t2}{t3}{t4}{t5}</ul>;
    $[37] = t1;
    $[38] = t2;
    $[39] = t3;
    $[40] = t4;
    $[41] = t5;
    $[42] = t11;
  } else {
    t11 = $[42];
  }
  let t12;
  if ($[43] !== T0 || $[44] !== t11 || $[45] !== t6 || $[46] !== t7) {
    t12 = <T0 className={t6}>{t7}{t11}</T0>;
    $[43] = T0;
    $[44] = t11;
    $[45] = t6;
    $[46] = t7;
    $[47] = t12;
  } else {
    t12 = $[47];
  }
  let t13;
  if ($[48] !== locale) {
    t13 = translate("deadlines", locale);
    $[48] = locale;
    $[49] = t13;
  } else {
    t13 = $[49];
  }
  let t14;
  if ($[50] !== t13) {
    t14 = <SectionHeading className="text-xl">{t13}</SectionHeading>;
    $[50] = t13;
    $[51] = t14;
  } else {
    t14 = $[51];
  }
  let t15;
  if ($[52] !== data.deadlines || $[53] !== due || $[54] !== locale) {
    t15 = <DeadlineTimeline deadlines={data.deadlines} due={due} locale={locale} />;
    $[52] = data.deadlines;
    $[53] = due;
    $[54] = locale;
    $[55] = t15;
  } else {
    t15 = $[55];
  }
  let t16;
  if ($[56] !== t14 || $[57] !== t15) {
    t16 = <Panel className="space-y-5">{t14}{t15}</Panel>;
    $[56] = t14;
    $[57] = t15;
    $[58] = t16;
  } else {
    t16 = $[58];
  }
  let t17;
  if ($[59] !== data.documents || $[60] !== locale || $[61] !== onOpenDocuments) {
    t17 = data.documents.length > 0 && <Panel className="@container space-y-2"><div className="flex flex-wrap items-baseline justify-between gap-2"><SectionHeading className="text-xl">{translate("areaDocuments", locale)}</SectionHeading>{data.documents.length > OVERVIEW_DOCUMENTS && <button type="button" className="text-conveniat-green min-h-11 cursor-pointer text-sm font-semibold underline underline-offset-2" onClick={onOpenDocuments}>{translate("allDocuments", locale)}</button>}</div><DocumentLinks documents={data.documents.slice(0, OVERVIEW_DOCUMENTS)} locale={locale} /></Panel>;
    $[59] = data.documents;
    $[60] = locale;
    $[61] = onOpenDocuments;
    $[62] = t17;
  } else {
    t17 = $[62];
  }
  let t18;
  if ($[63] !== t16 || $[64] !== t17) {
    t18 = <div className="space-y-6">{t16}{t17}</div>;
    $[63] = t16;
    $[64] = t17;
    $[65] = t18;
  } else {
    t18 = $[65];
  }
  let t19;
  if ($[66] !== t12 || $[67] !== t18 || $[68] !== t8) {
    t19 = <div className={t8}>{t12}{t18}</div>;
    $[66] = t12;
    $[67] = t18;
    $[68] = t8;
    $[69] = t19;
  } else {
    t19 = $[69];
  }
  let t20;
  if ($[70] !== t10 || $[71] !== t19 || $[72] !== t9) {
    t20 = <div className={t9}>{t10}{t19}</div>;
    $[70] = t10;
    $[71] = t19;
    $[72] = t9;
    $[73] = t20;
  } else {
    t20 = $[73];
  }
  return t20;
};
function _temp(t0) {
  const {
    progress
  } = t0;
  return isOpen(progress);
}
function _temp2(t0) {
  const {
    progress: progress_0
  } = t0;
  return progress_0.deadline !== undefined;
}
function _temp3(t0) {
  const {
    progress: progress_1
  } = t0;
  return progress_1.state === "done";
}
function _temp4(t0) {
  const {
    progress: progress_2
  } = t0;
  return progress_2.state === "done" ? "filled" : "empty";
}
function _temp5(entry) {
  return isOpen(entry.progress);
}
function _temp6(entry_0) {
  return entry_0.progress.state === "done";
}
function _temp7(entry_1) {
  return entry_1.progress.state === "closed";
}
function _temp8(entry_2) {
  return {
    deadline: entry_2.form.deadline,
    open: isOpen(entry_2.progress)
  };
}